/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { AppBar, createStyles, IconButton, ListItem, ListItemText, makeStyles, Theme, Toolbar, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import React, { useMemo, useRef, useState, useLayoutEffect, CSSProperties } from "react";
import { FixedSizeList as List } from "react-window";
import { UserDescription } from "../../redux/users";
import Search from "./Search";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flex: 1,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    toolbarButton: {
      marginRight: theme.spacing(2),
    },
    toolbarTitle: {
      marginRight: theme.spacing(4),
      flexShrink: 0,
    },
    search: {
      maxWidth: theme.breakpoints.width("sm"),
      width: "100%",
    },
    list: {
      height: "calc(100% + 96px)",
      WebkitOverflowScrolling: "touch",
    },
  })
);

interface usersSelectProps {
  onSelect: (user: UserDescription | null) => void;
  users: UserDescription[];
}

function usersSearchFilter(text: string) {
  // TODO: Improve performance for this comparison
  const exp = new RegExp(text, "i");
  return function (users: UserDescription) {
    return (
      exp.test(users.name)
    );
  };
}

const ITEM_SIZE = 52;

export default (props: usersSelectProps) => {
  const classes = useStyles(props);
  const [filter, setFilter] = useState<string>("");
  const { users } = props;

  const filteredUsers = useMemo(() => users.filter(usersSearchFilter(filter)), [users, filter]);

  const rootRef = useRef<HTMLDivElement>(null);

  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    setHeight((rootRef?.current as any).clientHeight);
  }, [height])

  return (
    <div className={classes.root}>
      <AppBar position="sticky">
        <Toolbar color="primary" variant="dense">
          <IconButton
            className={classes.toolbarButton}
            edge="start"
            color="inherit"
            onClick={() => props.onSelect(null)}
          >
            <CloseIcon />
          </IconButton>
          <Typography className={classes.toolbarTitle} variant="h6">
            Select user
          </Typography>
          <Search className={classes.search} onSearchUpdated={setFilter} showClear />
        </Toolbar>
      </AppBar>

      <div className={classes.list} ref={rootRef}>
        <List
          itemSize={ITEM_SIZE}
          itemCount={filteredUsers.length}
          height={height}
          width="100%"
        >
          {({ index, style }) => {
            const f = filteredUsers[index];

            return (
              <ListItem style={style as CSSProperties} key={f.name} button onClick={() => props.onSelect(f)}>
                <ListItemText
                  primary={f.name}
                />
              </ListItem>
            );
          }}
        </List>
      </div>
    </div>
  );
};
